// Per-viewer relevance for the admin security-alerts list (KAN-661, RD-SEC-046).
// Pure. Applied AFTER detection, with the viewer = the signed-in admin:
//  - an OPERATIONAL alert (mass void, bulk catalog delete, large credit
//    grant — KAN-784) whose ACTOR is the viewer never shows (you know what
//    you did);
//  - an IDENTITY alert (role / owner / admin status / 2FA) the viewer performed
//    STAYS, flagged `byViewer` so the row says "By you" (founder, 2026-10-07);
//  - an admin invite / promotion whose SUBJECT is the viewer never shows;
//  - 2FA-off and failed-sign-in alerts about the viewer DO show (relevant);
//  - an alert whose newest event predates the viewer's join date never shows.
// A missing viewer id fails toward SHOWING (never hides a real alert on a
// lookup gap) except the actor/subject checks, which need the id to match.

const lc = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

const OPERATIONAL_KINDS = new Set(['mass_void', 'bulk_catalog_delete', 'large_credit_grant']);

function isViewerSubjectOfRoleEscalation(event, viewer) {
  if (event.kind !== 'role_escalation') return false;
  if (viewer.userId && event.subjectUserId && event.subjectUserId === viewer.userId) return true;
  // No user id on the row (older invites) → match the invitee email.
  if (!event.subjectUserId && viewer.email && lc(event.subjectEmail) === lc(viewer.email)) return true;
  return false;
}

/**
 * @param {Array} events
 * @param {{ userId?: string|null, email?: string|null, joinedAt?: string|null }} viewer
 */
export function filterEventsForViewer(events, viewer = {}) {
  const joinedMs = viewer.joinedAt ? Date.parse(viewer.joinedAt) : NaN;
  const out = [];
  (events || []).forEach((event) => {
    const byViewer = Boolean(viewer.userId && event.actorId && event.actorId === viewer.userId);
    if (byViewer && OPERATIONAL_KINDS.has(event.kind)) return;
    if (isViewerSubjectOfRoleEscalation(event, viewer)) return;
    if (Number.isFinite(joinedMs)) {
      const seenMs = Date.parse(event.lastSeen);
      if (Number.isFinite(seenMs) && seenMs < joinedMs) return;
    }
    out.push(byViewer ? { ...event, byViewer: true } : event);
  });
  return out;
}

/** User ids whose NAME an event still needs (null headline name + a known id). */
export function collectMissingNameIds(events) {
  const ids = new Set();
  (events || []).forEach((e) => {
    const v = e.headline?.values || {};
    if (v.target == null && e.subjectUserId) ids.add(e.subjectUserId);
    if (v.actor == null && e.actorId) ids.add(e.actorId);
    if (e.nameRefs?.from) ids.add(e.nameRefs.from);
    if (e.nameRefs?.to) ids.add(e.nameRefs.to);
  });
  return Array.from(ids);
}

/** Fills null headline names from `names` (Map<userId, fullName>). A name that
 *  stays unknown stays null — the formatter renders the i18n "Someone". */
export function fillEventNames(events, names) {
  return (events || []).map((e) => {
    const v = { ...(e.headline?.values || {}) };
    if (v.target === null && e.subjectUserId) v.target = names.get(e.subjectUserId) || null;
    if (v.actor === null && e.actorId) v.actor = names.get(e.actorId) || null;
    if (e.nameRefs) {
      if (e.nameRefs.from) v.from = names.get(e.nameRefs.from) || v.from || null;
      if (e.nameRefs.to) v.to = names.get(e.nameRefs.to) || null;
    }
    return { ...e, headline: { ...e.headline, values: v } };
  });
}
